import { haversineMeters, bearingDegrees } from './geo';
import { comparePk } from './pkCompare';

/**
 * PK sin cobertura, a partir de la malla de la carretera precacheada
 * (`roadMeshStore`). Se usa cuando `pkByCoordinates` —que lo resuelve en
 * el backend— no contesta.
 *
 * ─── Por qué NO vale el vértice más cercano ────────────────────────────────
 *
 * La primera versión copiaba el algoritmo del backend a medias: vértice más
 * cercano, umbral de 300 m. Medido en beta con un recorrido real de la C-25
 * (82+800 → 235+800), la malla que devuelve el servidor trae **155 vértices
 * para 153 km**: uno por kilómetro.
 *
 * Con esa separación, un operario circulando JUSTO por la carretera está a menos
 * de 300 m de un vértice solo unos 600 m de cada 1.022 — o sea que el PK
 * **fallaba ~7 de cada 10 veces**, y tenía que teclearlo a mano, sin cobertura y
 * conduciendo. Y cuando acertaba, devolvía el PK del vértice: hasta medio
 * kilómetro de error.
 *
 * La densidad de la malla no es algo que podamos fijar desde aquí: el backend
 * devuelve N puntos para el rango pedido, así que cuanto más largo el recorrido,
 * más dispersa. Un umbral fijo nunca va a funcionar contra eso.
 *
 * ─── Lo que hace ahora ─────────────────────────────────────────────────────
 *
 * Proyecta la posición sobre el TRAMO entre cada par de vértices consecutivos e
 * interpola el PK — que es lo que hace el backend. Dos consecuencias:
 *
 *  - el PK sale con precisión de metros, independientemente de la densidad;
 *  - la distancia que se compara con el umbral pasa a ser la **perpendicular a
 *    la carretera**, que es lo que el umbral siempre quiso decir: "¿está el
 *    operario sobre la vía?". Estar a mitad de camino entre dos vértices ya no
 *    cuenta como estar lejos.
 *
 * El umbral se queda: un PK inventado es peor que ninguno. Si la patrulla está
 * de verdad a 2 km de la carretera, lo correcto es no responder.
 */
export type PkMeshVertex = {
  pkKm: number;
  pkMeter: number;
  latitude: number;
  longitude: number;
};

export type PkFromMeshResult = {
  pkKm: number;
  pkMeter: number;
  /** Distancia a la carretera (perpendicular al tramo), en metros. */
  distance: number;
  /**
   * Rumbo del eje de la carretera en el punto proyectado, en grados y **hacia
   * PK creciente**. `null` cuando la malla no tiene dos vértices con los que
   * medirlo.
   *
   * Lo consume la señal S2 del detector de cambio de sentido: si el rumbo real
   * de la patrulla se opone al que tendría circulando en el sentido que dice el
   * PK, es que la malla mintió y no se confirma nada. Va aquí, y no en un
   * helper aparte, porque el rumbo que importa es el del tramo de malla que
   * GANÓ la proyección — calcularlo fuera obligaría a repetir la búsqueda.
   */
  axisBearing: number | null;
  /**
   * Metros de asfalto recorridos desde el primer vértice de la malla hasta el
   * punto proyectado.
   *
   * 🔴 Existe porque el PK **no sirve para medir avance**: hay kilómetros
   * largos, y entre `84+1800` y `85+000` el total en metros BAJA 800 mientras
   * la patrulla avanza 90. El detector de cambio de sentido acumula evidencia
   * en metros hasta 500, así que esa bajada le fabricaba una vuelta que nadie
   * dio. Esto es una posición monótona de verdad.
   */
  alongMeters: number;
};



/**
 * Proyección sobre el tramo A→B en un plano local. A estas distancias (metros a
 * pocos kilómetros) la aproximación plana es exacta de sobra; usar geodésicas
 * complicaría el código sin cambiar un solo resultado.
 *
 * Devuelve dónde cae la proyección dentro del tramo (`t`, recortado a [0,1] para
 * no salirse por los extremos) y a qué distancia queda el punto.
 */
function proyectarEnTramo(
  lat: number,
  lng: number,
  a: PkMeshVertex,
  b: PkMeshVertex
): { t: number; distancia: number; largo: number } {
  const M_POR_GRADO_LAT = 111_320;
  const mPorGradoLng = M_POR_GRADO_LAT * Math.cos((lat * Math.PI) / 180);

  const ax = 0;
  const ay = 0;
  const bx = (b.longitude - a.longitude) * mPorGradoLng;
  const by = (b.latitude - a.latitude) * M_POR_GRADO_LAT;
  const px = (lng - a.longitude) * mPorGradoLng;
  const py = (lat - a.latitude) * M_POR_GRADO_LAT;

  const largo2 = (bx - ax) ** 2 + (by - ay) ** 2;
  if (largo2 === 0) {
    // Dos vértices en el mismo sitio: no hay tramo, solo el punto.
    return { t: 0, distancia: Math.hypot(px, py), largo: 0 };
  }

  const bruto = (px * bx + py * by) / largo2;
  const t = Math.min(1, Math.max(0, bruto));
  const distancia = Math.hypot(px - bx * t, py - by * t);
  return { t, distancia, largo: Math.sqrt(largo2) };
}

/** El mismo resultado, diciendo además en qué carretera cayó. */
export type PkFromMeshesResult = PkFromMeshResult & { roadId: string };

/**
 * Multi-carretera (2026-08-15): por debajo de esta diferencia entre las dos
 * mejores carreteras la elección no es fiable y manda `preferredRoadId`.
 *
 * Mismo umbral que el backend, y por el mismo motivo medido: la C-25 y su ramal
 * C-25z comparten calzada del PK 234+000 al 234+400 con sus vértices a 10-48 m,
 * y a partir de la bifurcación se separan 624 m en el 234+800. Con 50 m el tramo
 * compartido queda protegido y, en cuanto se bifurcan, la distancia decide sola.
 */
export const AMBIGUITY_MARGIN_METERS = 50;

/**
 * Hasta dónde se acepta que un punto «está sobre la vía», en metros
 * perpendiculares al eje.
 *
 * Espejo de `PK_MATCH_THRESHOLD_METERS` del backend, que nació en 100 y se
 * subió a 300 tras la prueba de carretera del 23-jul-2026 — el caso del
 * surtidor y las salidas largas. Estaba escrito como literal en las cuatro
 * firmas de este fichero y en una constante propia del botón de autolocalizar:
 * cinco sitios donde cambiar un número que es uno solo.
 */
export const PK_MATCH_MAX_METERS = 300;

/**
 * PK sin cobertura sobre VARIAS carreteras: corre `pkFromMesh` en cada malla y
 * se queda con la más cercana, diciendo además cuál ganó.
 *
 * Es el equivalente offline de `pkByCoordinates` con `roadIds`, y sale casi
 * gratis porque `pkFromMesh` ya devuelve la distancia perpendicular a la vía —
 * el desempate es comparar números que ya se calculan.
 *
 * 🔴 En el tramo que una carretera y su ramal COMPARTEN los dos candidatos
 * quedan a metros, y ahí elegir por distancia sería tirar una moneda: si la
 * diferencia no llega a `AMBIGUITY_MARGIN_METERS` gana `preferredRoadId` (la
 * del tramo en curso). No cambia el PK —en ese tramo es el mismo en las dos—
 * pero evita que el tramo salte de carretera por el temblor del GPS.
 */
export function pkFromMeshes(
  lat: number,
  lng: number,
  meshesByRoadId: Record<string, PkMeshVertex[]>,
  preferredRoadId?: string | null,
  maxMeters = PK_MATCH_MAX_METERS
): PkFromMeshesResult | null {
  const candidates: PkFromMeshesResult[] = [];
  for (const [roadId, mesh] of Object.entries(meshesByRoadId)) {
    const hit = pkFromMesh(lat, lng, mesh, maxMeters);
    if (hit) candidates.push({ ...hit, roadId });
  }
  return pickRoadWithPreference(candidates, preferredRoadId);
}

/**
 * Gana la más cercana, salvo empate técnico: si `preferredRoadId` está a menos
 * de `AMBIGUITY_MARGIN_METERS` de la ganadora, manda ella.
 *
 * Extraído de `pkFromMeshes` al aparecer el segundo llamador
 * (`pkFromMeshEntries`), y genérico desde el tercero (el botón de autolocalizar
 * PK, que trae candidatas del backend y no de la malla). La regla es la misma y
 * tiene que seguir siéndolo: dos criterios distintos para elegir carretera
 * acabarían discrepando justo en el tramo compartido, que es donde importa.
 */
export function pickRoadWithPreference<
  T extends { roadId: string; distance: number },
>(candidates: T[], preferredRoadId?: string | null): T | null {
  if (candidates.length === 0) return null;

  const ordenados = [...candidates].sort((a, b) => a.distance - b.distance);
  const [winner] = ordenados;

  if (preferredRoadId) {
    const preferred = ordenados.find((c) => c.roadId === preferredRoadId);
    if (
      preferred &&
      preferred.distance - winner.distance < AMBIGUITY_MARGIN_METERS
    ) {
      return preferred;
    }
  }

  return winner;
}

/** Una malla, con la carretera y la calzada a la que pertenece. */
export type PkFromMeshEntry = {
  roadId: string;
  direction: string;
  vertices: PkMeshVertex[];
};

/**
 * Las candidatas —una por CARRETERA— proyectando sobre cada calzada por
 * separado.
 *
 * 🔴 Por separado y no todo junto: las dos calzadas de una vía son polilíneas
 * paralelas, y concatenarlas crea tramos que cruzan de una a otra, con
 * proyecciones que no significan nada. Es la diferencia con `pkFromMeshes`, que
 * recibe UNA polilínea por carretera.
 *
 * Se colapsa a la mejor por carretera porque al operario le importa en qué vía
 * está, no en qué calzada: las dos dan prácticamente el mismo PK.
 *
 * Sin recortar por umbral cuando se pasa `Infinity`, que es lo que necesita
 * quien tiene que poder decir a qué distancia quedó la más cercana.
 */
export function pkCandidatesFromMeshEntries(
  lat: number,
  lng: number,
  entries: PkFromMeshEntry[],
  maxMeters = PK_MATCH_MAX_METERS
): PkFromMeshesResult[] {
  const mejorPorVia = new Map<string, PkFromMeshesResult>();
  for (const entry of entries) {
    const hit = pkFromMesh(lat, lng, entry.vertices, maxMeters);
    if (!hit) continue;
    const previo = mejorPorVia.get(entry.roadId);
    if (previo && previo.distance <= hit.distance) continue;
    mejorPorVia.set(entry.roadId, { ...hit, roadId: entry.roadId });
  }
  return [...mejorPorVia.values()];
}

/**
 * PK sin cobertura sobre mallas separadas POR CALZADA, que es como las guarda
 * `roadMeshStore`.
 *
 * Equivale a `pkFromMeshes` pero con la entrada abierta por sentido, y es lo
 * que hace que cambiar de sentido a mitad de recorrido deje de quedarse sin
 * malla: antes solo se cacheaba la calzada del tramo activo.
 */
export function pkFromMeshEntries(
  lat: number,
  lng: number,
  entries: PkFromMeshEntry[],
  preferredRoadId?: string | null,
  maxMeters = PK_MATCH_MAX_METERS
): PkFromMeshesResult | null {
  return pickRoadWithPreference(
    pkCandidatesFromMeshEntries(lat, lng, entries, maxMeters),
    preferredRoadId
  );
}

/**
 * El PK del punto que cae al `t` por ciento del camino entre dos vértices.
 *
 * 🔴 Interpolar el total en metros no vale: **hay kilómetros largos**. El 84 de
 * la C-17 mide 1.890 m, así que la malla encadena `84+1800` y después `85+000`,
 * y repartiendo el total el salto entre esos dos vértices es de **−800 m**
 * cuando en el asfalto son **+90** — el PK sale corriendo hacia atrás justo
 * donde el operario avanza.
 *
 * - **Mismo kilómetro**: los metros son comparables y se interpolan como
 *   siempre. Es el 99 % de los casos y no cambia nada.
 * - **Kilómetros distintos**: la etiqueta pega un salto que no se puede
 *   repartir, así que se cuenta desde `a` a razón de un metro de PK por metro
 *   de asfalto —que es lo que significa un PK— y al final se cae exactamente
 *   en `b`.
 *
 * Gemelo de `interpolarPk` en `rois-back/src/utils/pk-projection.helper.ts`.
 */
export function interpolarPk(
  a: PkMeshVertex,
  b: PkMeshVertex,
  t: number,
  largoMetros: number
): { pkKm: number; pkMeter: number } {
  if (a.pkKm === b.pkKm) {
    const metros = Math.round(a.pkMeter + (b.pkMeter - a.pkMeter) * t);
    return { pkKm: a.pkKm, pkMeter: Math.max(0, metros) };
  }
  if (t >= 1) return { pkKm: b.pkKm, pkMeter: b.pkMeter };
  const metros = Math.round(a.pkMeter + t * largoMetros);
  return { pkKm: a.pkKm, pkMeter: Math.max(0, metros) };
}

export function pkFromMesh(
  lat: number,
  lng: number,
  mesh: PkMeshVertex[],
  maxMeters = PK_MATCH_MAX_METERS
): PkFromMeshResult | null {
  if (mesh.length === 0) return null;

  if (mesh.length === 1) {
    const unico = mesh[0];
    const distancia = haversineMeters(lat, lng, unico.latitude, unico.longitude);
    return distancia > maxMeters
      ? null
      : {
          pkKm: unico.pkKm,
          pkMeter: unico.pkMeter,
          distance: distancia,
          axisBearing: null,
          alongMeters: 0,
        };
  }

  let mejor: PkFromMeshResult | null = null;
  // Metros acumulados hasta el vértice `i`. Se lleva al vuelo porque el bucle
  // ya recorre todos los tramos: la posición monótona sale gratis.
  let acumulado = 0;
  for (let i = 0; i < mesh.length - 1; i++) {
    const a = mesh[i];
    const b = mesh[i + 1];
    const { t, distancia, largo } = proyectarEnTramo(lat, lng, a, b);
    const hastaAqui = acumulado;
    acumulado += largo;
    if (mejor && distancia >= mejor.distance) continue;
    // El rumbo se toma SIEMPRE hacia PK creciente, no en el orden en que
    // vengan los vértices: es el eje del dato, no el de la lectura.
    //
    // 🔴 Con `comparePk`, no con el total en metros. Entre `84+1800` y `85+000`
    // el total dice que `b` es MENOR y el rumbo salía apuntando al revés — y ese
    // rumbo es la señal S2 del detector de cambio de sentido, que se dedica
    // precisamente a comparar hacia dónde va la patrulla contra el eje.
    const rumboEje =
      comparePk(
        { km: b.pkKm, meter: b.pkMeter },
        { km: a.pkKm, meter: a.pkMeter }
      ) >= 0
        ? bearingDegrees(a.latitude, a.longitude, b.latitude, b.longitude)
        : bearingDegrees(b.latitude, b.longitude, a.latitude, a.longitude);
    const { pkKm, pkMeter } = interpolarPk(a, b, t, largo);
    mejor = {
      pkKm,
      pkMeter,
      distance: distancia,
      axisBearing: rumboEje,
      alongMeters: hastaAqui + t * largo,
    };
  }

  if (!mejor || mejor.distance > maxMeters) return null;
  return mejor;
}

export default pkFromMesh;
